import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "stores";
import {
  Product,
  ProductBom,
  BomComponent,
  ProductBomFormData,
  BOMFormData,
} from "./types";

interface SelectedComponent {
  componentId: string;
  quantity: number;
}

class BOMViewStore {
  // Observable state
  currentStep: number = 1;
  editMode: boolean = false;
  selectedProductId: string | null = null;
  selectedComponents: SelectedComponent[] = [];
  bomData: BOMFormData = {
    productId: "",
    notes: "",
  };
  isLoading: boolean = false;
  error: string | null = null;
  savedProductBomItems: ProductBom[] = [];
  showComponentDialog: boolean = false;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false } as any);
  }

  // Actions
  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  setSelectedProduct(productId: string | null) {
    this.selectedProductId = productId;
    if (productId) {
      this.bomData.productId = productId;
    }
  }

  updateBOMData(data: Partial<BOMFormData>) {
    this.bomData = { ...this.bomData, ...data };
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  setEditMode(editMode: boolean) {
    this.editMode = editMode;
    if (editMode) {
      this.selectedProductId = null;
      this.selectedComponents = [];
      this.bomData = {
        productId: "",
        notes: "",
      };
    }
  }

  setShowComponentDialog(show: boolean) {
    this.showComponentDialog = show;
  }

  addSelectedComponent(componentId: string, quantity: number) {
    const existingIndex = this.selectedComponents.findIndex(
      (item) => item.componentId === componentId
    );
    if (existingIndex >= 0) {
      this.selectedComponents[existingIndex].quantity = quantity;
    } else {
      this.selectedComponents.push({ componentId, quantity });
    }
  }

  removeSelectedComponent(componentId: string) {
    this.selectedComponents = this.selectedComponents.filter(
      (item) => item.componentId !== componentId
    );
  }

  updateSelectedComponentQuantity(componentId: string, quantity: number) {
    const item = this.selectedComponents.find((item) => item.componentId === componentId);
    if (item) {
      item.quantity = quantity;
    }
  }

  reset() {
    this.currentStep = 1;
    this.editMode = false;
    this.selectedProductId = null;
    this.selectedComponents = [];
    this.bomData = {
      productId: "",
      notes: "",
    };
    this.isLoading = false;
    this.error = null;
    this.savedProductBomItems = [];
    this.showComponentDialog = false;
  }

  // Async actions
  async loadExistingBOM() {
    if (!this.selectedProductId) {
      this.setError("Please select a product to edit");
      return;
    }

    this.isLoading = true;
    this.clearError();

    try {
      // Load the product BOM items for this product
      const productBomResponse = await this.productBomStore.find({
        query: {
          productId: this.selectedProductId,
        },
      });

      runInAction(() => {
        if (productBomResponse?.data) {
          this.savedProductBomItems = productBomResponse.data;
          // Convert to selected components format
          this.selectedComponents = productBomResponse.data.map((item: ProductBom) => ({
            componentId: item.componentId,
            quantity: item.quantity,
          }));
        }

        this.isLoading = false;
        this.currentStep = 2;
      });

      return this.savedProductBomItems;
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to load BOM");
        this.isLoading = false;
      });
      throw error;
    }
  }

  async saveBOMItems() {
    if (!this.selectedProductId) {
      this.setError("No product selected");
      return;
    }

    if (this.selectedComponents.length === 0) {
      this.setError("No components selected");
      return;
    }

    this.isLoading = true;
    this.clearError();

    try {
      // If editing, first delete existing items
      if (this.editMode && this.savedProductBomItems.length > 0) {
        for (const item of this.savedProductBomItems) {
          await this.productBomStore.remove(item.id);
        }
      }

      // Create new product BOM items
      const itemsPayload: ProductBomFormData[] = this.selectedComponents.map((selected, index) => ({
        productId: this.selectedProductId!,
        componentId: selected.componentId,
        quantity: selected.quantity,
        sortOrder: index,
        notes: this.bomData.notes || undefined,
      }));

      console.log('[BOM] Creating items with payload:', itemsPayload);

      const createdItems = await this.productBomStore.create(itemsPayload);

      runInAction(() => {
        if (createdItems) {
          this.savedProductBomItems = Array.isArray(createdItems)
            ? createdItems.filter((item: ProductBom | null) => item != null)
            : createdItems ? [createdItems] : [];
        } else {
          this.savedProductBomItems = [];
        }
        this.isLoading = false;
      });

      return this.savedProductBomItems;
    } catch (error: any) {
      console.error('[BOM] Failed to save items:', error);

      let errorMessage = "Failed to save BOM items";
      if (error.message) {
        errorMessage = error.message;
      }
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      }
      if (error.errors && error.errors.length > 0) {
        errorMessage = error.errors.map((e: any) => e.message || e).join(', ');
      }

      runInAction(() => {
        this.setError(errorMessage);
        this.isLoading = false;
      });
      throw error;
    }
  }

  async submitForApproval() {
    // For product BOM, this is essentially just confirming the BOM is complete
    this.isLoading = true;
    this.clearError();

    try {
      // The BOM items are already saved, just move to the final step
      runInAction(() => {
        this.isLoading = false;
      });

      return this.savedProductBomItems;
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to submit BOM for approval");
        this.isLoading = false;
      });
      throw error;
    }
  }

  // Computed properties
  get selectedProduct(): Product | null {
    if (!this.selectedProductId) return null;
    return (this.rootStore as any).productsStore?.entities?.find(
      (p: Product) => p.id === this.selectedProductId
    ) || null;
  }

  get products(): Product[] {
    return (this.rootStore as any).productsStore?.entities || [];
  }

  get availableComponents(): BomComponent[] {
    return (this.rootStore as any).bomComponentsStore?.entities || [];
  }

  // Helper to access product BOM store
  private get productBomStore() {
    return (this.rootStore as any).productBomStore;
  }

  get existingProductBoms(): ProductBom[] {
    return this.productBomStore?.entities || [];
  }

  get selectedComponentsDetails() {
    const componentsStore = (this.rootStore as any).bomComponentsStore;
    return this.selectedComponents.map((selected) => {
      const component = componentsStore?.entities?.find((c: BomComponent) => c.id === selected.componentId);
      if (!component) return null;
      return {
        id: component.id,
        componentCode: component.componentCode,
        componentName: component.componentName,
        quantity: selected.quantity,
        unitOfMeasure: component.unitOfMeasure || 'ea',
        unitCost: component.unitCost || 0,
        totalCost: selected.quantity * (component.unitCost || 0),
        category: component.category || 'component',
      };
    }).filter(item => item !== null);
  }

  get totalCost() {
    return this.selectedComponentsDetails.reduce((sum, item) => {
      return sum + (Number(item?.totalCost) || 0);
    }, 0);
  }

  get costByCategory() {
    const costs: Record<string, number> = {
      material: 0,
      component: 0,
      labor: 0,
      service: 0,
      tool: 0,
    };

    this.selectedComponentsDetails.forEach((item) => {
      if (!item || !item.category) return;
      const category = item.category.toLowerCase();
      if (category in costs) {
        costs[category] += item.totalCost || 0;
      }
    });

    return costs;
  }

  get isValid() {
    if (!this.selectedProductId) return false;
    if (this.selectedComponents.length === 0) return false;
    return true;
  }
}

export default BOMViewStore;
